import { useEffect, useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Anchor, RotateCcw, ChevronDown, ChevronUp, AlertTriangle,
  LifeBuoy, Phone, Check, ArrowLeft, Play, Sparkles, Plug,
} from "lucide-react";

/* ---------------- content ------------------------------------------------- */
// Two sets of steps – kept side-by-side so we can render the same wizard UI
// for both. Order matters: customers step through top → bottom.

const DEPARTURE_ITEMS = [
  { key: "trip_plan", no: "Plan for turen er klar", en: "Trip plan is ready" },
  { key: "weather", no: "Sjekk vær og vind", en: "Check weather and wind" },
  { key: "vests", no: "Sjekk at det er vester til alle ombord", en: "Check there is a life vest for everyone onboard" },
  { key: "water", no: "Sjekk at du har vann til turen", en: "Check you have enough water for the trip" },
  { key: "blackwater", no: "Sjekk status på blackwater (svartvann-tank)", en: "Check blackwater (sewage tank) status" },
  { key: "throttle_neutral_1", no: "Throttle i nøytral posisjon", en: "Throttle in neutral position" },
  {
    key: "charger_off",
    no: "Lader koblet fra",
    en: "Shore power charger disconnected",
    hint_no: "VIKTIG brannsikkerhet: Bak i båten – på setet hvor hovedladekabelen lagres (styrbord side) – sitter en knapp. Den MÅ stå i AV-posisjon når ladekabelen kobles til eller trekkes ut. Knappen skal kun presses inn (PÅ) ETTER at ladekabelen er ordentlig plugget inn.",
    hint_en: "IMPORTANT fire-safety: At the rear of the boat – on the seat where the main charging cable is stored (starboard side) – there is a button. It MUST be in the OFF position when the charging cable is plugged in or unplugged. Only press it in (ON) AFTER the cable is properly connected.",
    hint_warn: true,
  },
  { key: "ladder", no: "Sjekk at badestige er tatt opp", en: "Check the boarding ladder is up" },
  {
    key: "rear_hatch",
    no: "Hev evt. lem om den er nede (husk å stoppe ca. 1 cm fra topp – tauet ryker raskt om den dras helt inn)",
    en: "Raise the rear hatch if it is down (stop ~1 cm from the top – the cord breaks easily if it is pulled all the way in)",
  },
  { key: "engine_power_off", no: "Motor power-knapp av (sjekk)", en: "Engine power button OFF (verify)" },
  { key: "key_active", no: "Nøkkel vris til aktiv posisjon", en: "Turn the key to the active position" },
  { key: "panels_on", no: "Paneler aktiveres med brytere", en: "Activate the panels with the switches" },
  { key: "accept_terms", no: "Aksepter vilkår på skjermer", en: "Accept the terms on the screens" },
  { key: "lanterns", no: "Hvis solen har gått ned: slå lanterner på", en: "If the sun has set, turn the navigation lights on" },
  { key: "throttle_neutral_2", no: "Throttle i nøytral posisjon (sjekk)", en: "Throttle in neutral position (verify)" },
  { key: "engine_button_on", no: "Motorknapp på", en: "Engine button ON" },
  { key: "thrusters", no: "Thrustere aktiveres", en: "Activate the thrusters" },
  { key: "lines_off", no: "Tau tas av båten og er klar for avgang", en: "Untie the lines – ready for departure" },
  { key: "fenders_in", no: "Etter avgang tas fendere opp i båten", en: "Once underway, bring the fenders inside the boat" },
  { key: "enjoy", no: "Nyt turen", en: "Enjoy the trip" },
];

const DOCKING_ITEMS = [
  { key: "fenders_out", no: "Legg ut fendere", en: "Put out the fenders" },
  { key: "pick_spot", no: "Bestem deg for hvor du skal stå", en: "Decide where you'll dock" },
  { key: "thrusters_on", no: "Aktiver thrustere", en: "Activate the thrusters" },
  { key: "tie_up", no: "Fest båten med tau", en: "Tie the boat up with lines" },
  {
    key: "screens_off",
    no: "Slå av alle skjermer med hovedbrytere – la tenningen være på",
    en: "Turn off all screens with the master switches – leave the ignition ON",
  },
  {
    key: "charger_button_off",
    no: "Sjekk at ladeknapp er i AV-posisjon",
    en: "Check that the charging button is in the OFF position",
    hint_no: "Bak i båten (styrbord side, ved ladekabel-rommet). Knappen MÅ stå av før du kobler til laderen – brannsikkerhet.",
    hint_en: "Rear of the boat (starboard, near the cable locker). The button MUST be OFF before you plug in the charger – fire safety.",
    hint_warn: true,
  },
  {
    key: "cable_check",
    no: "Skal du bruke vanlig landstrøm 230V? Sjekk at den store og den lille kabelen er koblet sammen i ladekabel-rommet",
    en: "Using 230V shore power? Check that the large and the small cable are connected together inside the cable locker",
  },
  { key: "plug_in_press", no: "Koble til lader – trykk pluggen ordentlig inn til den sitter godt fast",
    en: "Plug in the charger – push it firmly in until it's fully seated",
    hint_no: "VIKTIG: Trykk pluggen inn både i båten og i landkoblingen. En løs eller dårlig innkoblet plugg kan overopphetes og gi brannfare. Sjekk at den ikke er løs.",
    hint_en: "IMPORTANT: Push the plug fully in – both at the boat and at the shore socket. A loose or poorly seated plug can overheat and cause a fire. Make sure it isn't loose.",
    hint_warn: true },
  { key: "safety_ring_locked", no: "Sikre høyvolt-pluggen med sikkerhetsringen – vri den til den er låst",
    en: "Secure the high-voltage plug with the safety ring – twist until locked",
    hint_no: "Gjelder kun kabelen med sikkerhetsring (høyvolt). Uten låst ring kan pluggen løsne under lading og forårsake brannfare. Hopp over dette punktet dersom kabelen din ikke har sikkerhetsring.",
    hint_en: "Only applies to cables with a safety ring (high-voltage). Without a locked ring the plug can come loose during charging and become a fire hazard. Skip this step if your cable has no safety ring.",
    hint_warn: true },
  { key: "press_charger_button", no: "Trykk deretter på ladeknappen for å starte lading",
    en: "Then press the charging button to start charging" },
  { key: "verify_charging", no: "Sjekk at ladeknappen lyser og at båten faktisk lader", en: "Check that the charging button is lit and the boat is actually charging" },
  { key: "ignition_off", no: "Slå av tenningen", en: "Turn off the ignition" },
];

/* ---------------- localStorage state ------------------------------------- */
const STORAGE_KEY = "cbt:customer_checklists_v2";

function loadAllState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
}
function saveAllState(all) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(all)); }
  catch { /* private mode */ }
}
function loadState(bookingId, listKey) {
  if (!bookingId) return { checked: {}, cursor: 0 };
  const all = loadAllState();
  return (all[bookingId] || {})[listKey] || { checked: {}, cursor: 0 };
}
function saveState(bookingId, listKey, state) {
  if (!bookingId) return;
  const all = loadAllState();
  all[bookingId] = { ...(all[bookingId] || {}), [listKey]: state };
  saveAllState(all);
}

/* ---------------- reusable wizard --------------------------------------- */
function StepWizard({
  bookingId, listKey, items, lang, testId,
  title, subtitle, tone = "gold", icon: Icon,
}) {
  const [state, setState] = useState({ checked: {}, cursor: 0, started: false });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const s = loadState(bookingId, listKey);
    setState({ checked: s.checked || {}, cursor: s.cursor || 0, started: !!s.started });
  }, [bookingId, listKey]);

  const persist = (next) => {
    setState(next);
    saveState(bookingId, listKey, next);
  };

  const total = items.length;
  const doneCount = useMemo(
    () => items.filter((i) => state.checked[i.key]).length,
    [items, state.checked],
  );
  const allDone = doneCount === total;
  // "In progress" if the user has explicitly hit Start or already ticked at least one item.
  const hasStarted = state.started || doneCount > 0;

  // Cursor logic: next unchecked from the top, capped at last item.
  const cursor = Math.min(state.cursor ?? 0, total - 1);
  const current = items[cursor];
  const currentDone = current && !!state.checked[current.key];

  const advance = (fromIdx) => {
    // Find next unchecked step after fromIdx, else stop at last index.
    for (let i = fromIdx + 1; i < items.length; i++) {
      if (!state.checked[items[i].key]) return i;
    }
    return items.length - 1;
  };

  const markCurrentDone = () => {
    if (!current) return;
    const nextChecked = { ...state.checked, [current.key]: true };
    // Compute next cursor considering the fresh state, not `state.checked`.
    let nextIdx = cursor;
    for (let i = cursor + 1; i < items.length; i++) {
      if (!nextChecked[items[i].key]) { nextIdx = i; break; }
      nextIdx = i;
    }
    if (Object.keys(nextChecked).length === items.length &&
        items.every((it) => nextChecked[it.key])) {
      nextIdx = items.length - 1; // stay put, we're done
    }
    persist({ checked: nextChecked, cursor: nextIdx, started: true });
  };

  const goPrev = () => {
    // Move cursor back one step. Un-check that step so user can re-do it.
    const prev = Math.max(cursor - 1, 0);
    const nextChecked = { ...state.checked };
    delete nextChecked[items[prev].key];
    persist({ checked: nextChecked, cursor: prev, started: true });
  };

  const reset = () => persist({ checked: {}, cursor: 0, started: false });

  const start = () => {
    setExpanded(true);
    persist({
      checked: state.checked,
      cursor: advance(-1),
      started: true,
    });
  };

  const T = lang === "no" ? {
    step: "Steg",
    of: "av",
    ok_next: "OK – neste",
    prev: "Forrige",
    reset: "Start på nytt",
    start: "Start sjekkliste",
    resume: "Fortsett",
    close: "Skjul",
    not_started: "Ikke startet",
    all_done: "Fullført – godt jobbet!",
    remaining: (n) => `${n} steg gjenstår`,
  } : {
    step: "Step",
    of: "of",
    ok_next: "OK – next",
    prev: "Previous",
    reset: "Start over",
    start: "Start checklist",
    resume: "Continue",
    close: "Hide",
    not_started: "Not started",
    all_done: "All done – great job!",
    remaining: (n) => `${n} step${n === 1 ? "" : "s"} left`,
  };

  const toneStyles = {
    gold: {
      accent: "bg-[#BD984D]",
      accentText: "text-[#A57F39]",
      soft: "bg-[#BD984D]/10",
      border: "border-[#BD984D]/40",
      btn: "bg-[#BD984D] hover:bg-[#A57F39] text-white",
    },
    teal: {
      accent: "bg-[#1DA3B3]",
      accentText: "text-[#1DA3B3]",
      soft: "bg-[#1DA3B3]/10",
      border: "border-[#1DA3B3]/40",
      btn: "bg-[#1DA3B3] hover:bg-[#178791] text-white",
    },
  };
  const s = toneStyles[tone] || toneStyles.gold;

  const label = (it) => (lang === "no" ? it.no : it.en);
  const hint = (it) => (lang === "no" ? it.hint_no : it.hint_en);

  const progressPct = allDone ? 100 : Math.round((doneCount / total) * 100);

  return (
    <Card
      className={`overflow-hidden ${allDone ? "border-emerald-300" : ""}`}
      data-testid={testId}
    >
      {/* ------ collapsed header (always visible) ------ */}
      <button
        type="button"
        className={`w-full text-left p-5 sm:p-6 flex items-start gap-4 hover:${s.soft} transition-colors`}
        onClick={() => setExpanded((v) => !v)}
        data-testid={`${testId}-toggle`}
        aria-expanded={expanded}
      >
        <div className={`p-2.5 rounded-lg ${s.soft} ${s.accentText} shrink-0`}>
          {Icon ? <Icon className="w-5 h-5" /> : <Anchor className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold text-[#0C1824]">{title}</h2>
            {allDone && (
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                <Check className="w-3 h-3" /> {T.all_done}
              </span>
            )}
            {!allDone && hasStarted && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.soft} ${s.accentText}`}>
                {T.remaining(total - doneCount)}
              </span>
            )}
            {!hasStarted && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600">
                {T.not_started}
              </span>
            )}
          </div>
          <p className="text-sm text-[#2B3F4D] mt-1">{subtitle}</p>
          {/* Compact progress bar in the header */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full transition-all ${allDone ? "bg-emerald-500" : s.accent}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-1.5 text-[11px] font-mono text-[#2B3F4D]">
            {doneCount}/{total}
          </div>
        </div>
        <div className={`${s.accentText} shrink-0`}>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>

      {/* ------ expanded content ------ */}
      {expanded && (
        <div className="px-5 sm:px-6 pb-6" data-testid={`${testId}-body`}>
          {/* Not started – big "start" call to action */}
          {!hasStarted && !allDone && (
            <div className="text-center py-6">
              <Button
                onClick={start}
                className={s.btn}
                data-testid={`${testId}-start`}
              >
                <Play className="w-4 h-4 mr-2" />
                {T.start}
              </Button>
            </div>
          )}

          {/* All done – celebrate + reset */}
          {allDone && (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-5 text-center">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-500 text-white mb-2">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="text-emerald-800 font-semibold text-lg">
                {T.all_done}
              </div>
              <Button
                variant="outline"
                onClick={reset}
                className="mt-4 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid={`${testId}-reset`}
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> {T.reset}
              </Button>
            </div>
          )}

          {/* In progress – show current step big and bold */}
          {hasStarted && !allDone && current && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs uppercase tracking-widest">
                <span className={`font-semibold ${s.accentText}`}>
                  {T.step} {cursor + 1} {T.of} {total}
                </span>
                <button
                  type="button"
                  onClick={reset}
                  className="text-slate-500 hover:text-red-600 transition inline-flex items-center gap-1"
                  data-testid={`${testId}-reset-inline`}
                >
                  <RotateCcw className="w-3 h-3" /> {T.reset}
                </button>
              </div>

              <div className={`rounded-xl border-2 ${s.border} ${s.soft} p-5 sm:p-6`}>
                <div className="flex items-start gap-4">
                  <div
                    className={`h-10 w-10 rounded-full ${s.accent} text-white flex items-center justify-center font-bold text-lg shrink-0`}
                    data-testid={`${testId}-step-num`}
                  >
                    {cursor + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-lg sm:text-xl font-semibold text-[#0C1824] leading-snug"
                      data-testid={`${testId}-step-label`}
                    >
                      {label(current)}
                    </div>
                    {hint(current) && (
                      <div
                        className={`mt-3 text-sm leading-relaxed rounded-md p-3 ${
                          current.hint_warn
                            ? "bg-amber-50 border border-amber-200 text-amber-900 font-medium flex gap-2"
                            : "bg-white border border-slate-200 text-[#2B3F4D] italic"
                        }`}
                      >
                        {current.hint_warn && (
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                        )}
                        <span>{hint(current)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={goPrev}
                  disabled={cursor === 0}
                  data-testid={`${testId}-prev`}
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> {T.prev}
                </Button>
                <Button
                  onClick={markCurrentDone}
                  disabled={currentDone && cursor === total - 1}
                  className={`${s.btn} min-w-[140px]`}
                  data-testid={`${testId}-next`}
                  size="lg"
                >
                  <Check className="w-4 h-4 mr-1.5" /> {T.ok_next}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ---------------- top-level component ------------------------------------ */
export default function CustomerDepartureChecklist({ lang = "en", bookingId }) {
  const [openIncident, setOpenIncident] = useState(false);

  const T = lang === "no" ? {
    departure_title: "Avgangs-sjekkliste",
    departure_subtitle: "Klargjør båten før du legger fra kai – ett steg av gangen.",
    docking_title: "Legg til kai – lading",
    docking_subtitle: "Trygg fortøyning og korrekt oppstart av lading (brannsikkerhet).",
    incident_title: "Sjekkliste ved uhell",
    incident_subtitle: "Trykk for å åpne. Hold roen og ta én ting av gangen.",
    open: "Åpne",
    close: "Lukk",
    emergency: "Nødnummer",
    call_112: "Ring 112",
    call_120: "Ring Redningsselskapet 120",
    call_120_alt: "+47 915 02016",
  } : {
    departure_title: "Pre-departure checklist",
    departure_subtitle: "Get the boat ready to leave the dock – one step at a time.",
    docking_title: "Docking & charging",
    docking_subtitle: "Safe mooring and correct charging startup (fire safety).",
    incident_title: "Incident checklist",
    incident_subtitle: "Tap to open. Stay calm and take one step at a time.",
    open: "Open",
    close: "Close",
    emergency: "Emergency numbers",
    call_112: "Call 112",
    call_120: "Call Sea Rescue 120",
    call_120_alt: "+47 915 02016",
  };

  const incidentSteps = lang === "no" ? [
    { title: "1. Sjekk at alle har det bra", body: "Hvis ja, fortsett. Hvis nei og situasjonen er kritisk – ring 112 umiddelbart.", severity: "red" },
    { title: "2. Sjekk båtens tilstand – tar du inn vann?", body: "Hvis ja: sørg for at alle får på seg vester. Om mulig svøm eller ta dere til land. Hvis ikke – ring 112.", severity: "red" },
    { title: "3. Bruk VHF hvis det er ombord", body: "VHFen er vanntett og kan medbringes i vann. Meld redning og følg instrukser på kanal 16 (Mayday, Mayday, Mayday – navn – posisjon).", severity: "amber" },
    { title: "4. Hvis du står på grunn og ikke kommer av – IKKE RYGG", body: "Det kan suge sand, gjørme og stein inn i kjølesystemet eller forverre evt. skade. Stopp motoren.", severity: "amber" },
    { title: "5. Prøv manuell dytting", body: "Vurder tidevannet – hvis vannet stiger kan båten løftes av grunn. Tilkall profesjonell hjelp hvis nødvendig.", severity: "info" },
    { title: "6. Ring oss og del posisjon i app", body: "Vi anbefaler at du ringer oss og deler posisjon i app – så hjelper vi deg å vurdere situasjonen. Tar du inn vann eller det er fare for liv: ring 112. Ved behov for evakuering: ring Redningsselskapets nødnummer 120 (eller +47 915 02016).", severity: "info" },
    { title: "7. Mindre skader – du kan fortsette turen", body: "Del posisjonen med oss i app, ta bilde av skaden og skriv en kort rapport. Vi hjelper deg videre når du kommer tilbake.", severity: "info" },
  ] : [
    { title: "1. Check that everyone is OK", body: "If yes, continue. If no and the situation is critical – call 112 immediately.", severity: "red" },
    { title: "2. Check the boat – is it taking on water?", body: "If yes, make sure everyone puts on life vests. Swim or reach shore if possible. If not – call 112.", severity: "red" },
    { title: "3. Use the VHF if onboard", body: "The VHF is waterproof and can be taken into the water. Call for rescue on channel 16 (Mayday, Mayday, Mayday – name – position).", severity: "amber" },
    { title: "4. If you are aground and cannot get free – DO NOT REVERSE", body: "Reversing can suck sand, mud or stones into the cooling system or worsen the damage. Stop the engine.", severity: "amber" },
    { title: "5. Try manual pushing", body: "Consider the tide – if the water is rising, the boat may lift off. Call for professional help if needed.", severity: "info" },
    { title: "6. Call us and share your position in the app", body: "We recommend calling us and sharing your position via the app – we will help you assess the situation. If you're taking on water or there is danger to life: call 112. For evacuation: call the Sea Rescue emergency line 120 (or +47 915 02016).", severity: "info" },
    { title: "7. Minor damage – you can continue the trip", body: "Share your position with us in the app, take a photo of the damage and write a short report. We will assist you when you return.", severity: "info" },
  ];

  const sevStyle = {
    red: "border-l-red-500 bg-red-50",
    amber: "border-l-amber-500 bg-amber-50",
    info: "border-l-[#1DA3B3] bg-[#1DA3B3]/5",
  };

  return (
    <>
      <StepWizard
        bookingId={bookingId}
        listKey="departure"
        items={DEPARTURE_ITEMS}
        lang={lang}
        testId="customer-departure-checklist"
        title={T.departure_title}
        subtitle={T.departure_subtitle}
        tone="gold"
        icon={Anchor}
      />

      <StepWizard
        bookingId={bookingId}
        listKey="docking"
        items={DOCKING_ITEMS}
        lang={lang}
        testId="customer-docking-checklist"
        title={T.docking_title}
        subtitle={T.docking_subtitle}
        tone="teal"
        icon={Plug}
      />

      {/* Incident checklist – informational, collapsed by default */}
      <Card className="border-l-4 border-l-red-500" data-testid="customer-incident-checklist">
        <button
          type="button"
          className="w-full flex items-start justify-between gap-3 text-left p-5 sm:p-6"
          onClick={() => setOpenIncident((v) => !v)}
          data-testid="incident-toggle"
          aria-expanded={openIncident}
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-md text-red-600 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#0C1824]">{T.incident_title}</h2>
              <p className="text-sm text-[#2B3F4D] mt-0.5">{T.incident_subtitle}</p>
            </div>
          </div>
          <div className="shrink-0 text-[#A57F39]">
            {openIncident ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>

        {openIncident && (
          <div className="px-5 sm:px-6 pb-6 space-y-3" data-testid="incident-body">
            <div className="p-4 rounded-md bg-[#0C1824] text-white">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#ECC974] mb-2">
                {T.emergency}
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href="tel:112"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
                  data-testid="incident-call-112"
                >
                  <Phone className="w-4 h-4" /> {T.call_112}
                </a>
                <a
                  href="tel:120"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#BD984D] hover:bg-[#A57F39] text-white text-sm font-semibold"
                  data-testid="incident-call-120"
                >
                  <Phone className="w-4 h-4" /> {T.call_120}
                </a>
                <a
                  href="tel:+4791502016"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-mono"
                >
                  <Phone className="w-4 h-4" /> {T.call_120_alt}
                </a>
              </div>
            </div>

            {incidentSteps.map((step, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-md border-l-4 ${sevStyle[step.severity]}`}
                data-testid={`incident-step-${idx + 1}`}
              >
                <div className="font-semibold text-[#0C1824] text-sm">{step.title}</div>
                <div className="text-sm text-[#2B3F4D] mt-1 leading-relaxed">{step.body}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
